import { useParams, Navigate } from "react-router-dom";
import {
  Alert,
  AlertContent,
  AlertTitle,
  AlertDescription,
  AlertIndicator,
  Spinner,
} from "@heroui/react";
import {
  getCampaignById,
  useCampaignDetailsQuery,
  useCampaignMetricsQuery,
  useCampaignActionsQuery,
  formatCampaignType,
  formatPlacementTypes,
  type Campaign,
  type MetricItem,
  type CampaignDailyMetric,
} from "@/entities/campaign";
import { CampaignHeader } from "@/widgets/campaign-header";
import { CampaignMetrics } from "@/widgets/campaign-metrics";
import { OptimizationModules } from "@/widgets/optimization-modules";
import { CampaignHistory } from "@/widgets/campaign-history";

export const CampaignDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: realCampaign, isLoading: isCampaignLoading } =
    useCampaignDetailsQuery(id);
  // Запрашиваем 14 дней метрик для точного расчета динамики текущей и предыдущей недели
  const { data: metricsHistory, isLoading: isMetricsLoading } =
    useCampaignMetricsQuery(id, 14);
  const { data: actionsResponse, isLoading: isActionsLoading } =
    useCampaignActionsQuery(id);

  const fallback = getCampaignById(id || "1");

  if (isCampaignLoading || isMetricsLoading) {
    return (
      <div className="py-24 flex flex-col items-center justify-center gap-3">
        <Spinner size="lg" />
        <p className="text-xs text-neutral-400">
          Загрузка актуальных показателей кампании...
        </p>
      </div>
    );
  }

  // Расчет актуальных 4 карточек метрик по реальным данным из Директа за неделю (7 дней)
  const stats = realCampaign?.weekly_stats;
  const prevStats = realCampaign?.previous_weekly_stats;

  // Объединяем ежедневные данные из детальной кампании и отчета
  const allDays: CampaignDailyMetric[] =
    realCampaign?.daily_stats && realCampaign.daily_stats.length > 0
      ? realCampaign.daily_stats
      : metricsHistory || [];

  const sortedDays = allDays
    .slice()
    .sort(
      (a, b) =>
        new Date(a.report_date).getTime() - new Date(b.report_date).getTime(),
    );

  // Текущие 7 дней (неделя)
  const currentWeekDays = sortedDays.slice(-7);
  // Предыдущие 7 дней (прошлая неделя для сравнения динамики)
  const prevWeekDays =
    sortedDays.length > 7
      ? sortedDays.slice(Math.max(0, sortedDays.length - 14), -7)
      : [];

  let liveMetrics: MetricItem[] | undefined;

  const weekCost =
    stats?.cost ??
    (currentWeekDays.length > 0
      ? currentWeekDays.reduce((acc, d) => acc + (d.cost || 0), 0)
      : 0);
  const weekClicks =
    stats?.clicks ??
    (currentWeekDays.length > 0
      ? currentWeekDays.reduce((acc, d) => acc + (d.clicks || 0), 0)
      : 0);
  const weekImpressions =
    stats?.impressions ??
    (currentWeekDays.length > 0
      ? currentWeekDays.reduce((acc, d) => acc + (d.impressions || 0), 0)
      : 0);
  const weekCtr =
    stats?.ctr ??
    (weekImpressions > 0
      ? Number(((weekClicks / weekImpressions) * 100).toFixed(2))
      : 0);
  const avgDailyCost =
    stats?.avg_daily_cost ??
    (currentWeekDays.length > 0
      ? Math.round(weekCost / currentWeekDays.length)
      : 0);
  const avgDailyClicks =
    stats?.avg_daily_clicks ??
    (currentWeekDays.length > 0
      ? Math.round(weekClicks / currentWeekDays.length)
      : 0);
  const weeklyBudget =
    stats?.budget ?? realCampaign?.weekly_budget_amount ?? null;

  // Конверсии и CPA за 7 дней
  const totalConversions = currentWeekDays.reduce(
    (acc, d) => acc + (d.conversions || 0),
    0,
  );
  const avgCPA =
    totalConversions > 0 ? Math.round(weekCost / totalConversions) : 0;

  // Показатели прошлой недели для расчета динамики
  const prevWeekCost =
    prevStats?.cost ??
    (prevWeekDays.length > 0
      ? prevWeekDays.reduce((acc, d) => acc + (d.cost || 0), 0)
      : 0);
  const prevWeekClicks =
    prevStats?.clicks ??
    (prevWeekDays.length > 0
      ? prevWeekDays.reduce((acc, d) => acc + (d.clicks || 0), 0)
      : 0);
  const prevWeekImpressions =
    prevStats?.impressions ??
    (prevWeekDays.length > 0
      ? prevWeekDays.reduce((acc, d) => acc + (d.impressions || 0), 0)
      : 0);
  const prevWeekCtr =
    prevStats?.ctr ??
    (prevWeekImpressions > 0
      ? Number(((prevWeekClicks / prevWeekImpressions) * 100).toFixed(2))
      : 0);
  const prevConversions = prevWeekDays.reduce(
    (acc, d) => acc + (d.conversions || 0),
    0,
  );
  const prevCPA =
    prevConversions > 0 ? Math.round(prevWeekCost / prevConversions) : 0;

  if (stats || currentWeekDays.length > 0) {
    const costPoints = currentWeekDays.map((d) => d.cost);
    const clicksPoints = currentWeekDays.map((d) => d.clicks);
    const ctrPoints = currentWeekDays.map((d) =>
      d.impressions > 0
        ? Number(((d.clicks / d.impressions) * 100).toFixed(2))
        : 0,
    );
    const conversionPoints = currentWeekDays.map((d) => d.conversions);

    // 1. Бюджет и расход за последние 7 дней
    const isOverbudget = Boolean(
      weeklyBudget && weeklyBudget > 0 && weekCost > weeklyBudget,
    );
    let costChangeStr: string | undefined;
    let isCostPositive = !isOverbudget;
    if (prevWeekCost > 0) {
      const diffCost = Math.round(
        ((weekCost - prevWeekCost) / prevWeekCost) * 100,
      );
      costChangeStr = `${diffCost >= 0 ? "+" : ""}${diffCost}%`;
      isCostPositive = !isOverbudget;
    }

    // 2. Клики за последние 7 дней
    let clicksChangeStr: string | undefined;
    let isClicksPositive = true;
    if (prevWeekClicks > 0) {
      const diffClicks = Math.round(
        ((weekClicks - prevWeekClicks) / prevWeekClicks) * 100,
      );
      clicksChangeStr = `${diffClicks >= 0 ? "+" : ""}${diffClicks}%`;
      isClicksPositive = diffClicks >= 0;
    } else if (avgDailyClicks > 0) {
      clicksChangeStr = `~${avgDailyClicks} в день`;
    }

    // 3. CTR за последние 7 дней
    let ctrChangeStr: string | undefined;
    let isCtrPositive = weekCtr >= 1.0;
    if (prevWeekCtr > 0) {
      const diffCtr = Number((weekCtr - prevWeekCtr).toFixed(2));
      ctrChangeStr = `${diffCtr >= 0 ? "+" : ""}${diffCtr}%`;
      isCtrPositive = diffCtr >= 0;
    }

    // 4. CPA за последние 7 дней
    let cpaChangeStr: string | undefined;
    let isCpaPositive = true;
    if (avgCPA > 0 && prevCPA > 0) {
      const diffCpa = Math.round(((avgCPA - prevCPA) / prevCPA) * 100);
      cpaChangeStr = `${diffCpa >= 0 ? "+" : ""}${diffCpa}%`;
      isCpaPositive = diffCpa <= 0; // Снижение CPA является положительной динамикой
    }

    const currentWeekDates = currentWeekDays.map((d) => {
      const dateObj = new Date(d.report_date);
      return dateObj.toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "short",
      });
    });

    liveMetrics = [
      {
        label: "Бюджет и расход",
        value: `${Math.round(weekCost).toLocaleString("ru-RU")} ₽`,
        subValue: weeklyBudget
          ? `из ${weeklyBudget.toLocaleString("ru-RU")} ₽/нед`
          : "Лимит не задан",
        change: costChangeStr,
        isPositive: isCostPositive,
        chartType: "line",
        chartPoints:
          costPoints.length >= 2
            ? costPoints
            : [weekCost * 0.8, weekCost * 0.9, weekCost],
        chartDates: currentWeekDates,
        unit: "₽",
        formatValue: (val: number) =>
          `${Math.round(val).toLocaleString("ru-RU")} ₽`,
      },
      {
        label: "Клики",
        value: weekClicks.toLocaleString("ru-RU"),
        subValue: `${weekImpressions.toLocaleString("ru-RU")} показов`,
        change: clicksChangeStr,
        isPositive: isClicksPositive,
        chartType: "line",
        chartPoints:
          clicksPoints.length >= 2
            ? clicksPoints
            : [weekClicks * 0.8, weekClicks * 0.9, weekClicks],
        chartDates: currentWeekDates,
        unit: "кликов",
        formatValue: (val: number) =>
          `${Math.round(val).toLocaleString("ru-RU")} кликов`,
      },
      {
        label: "Средний CTR",
        value: `${weekCtr}%`,
        subValue:
          avgDailyCost > 0
            ? `~${Math.round(avgDailyCost).toLocaleString("ru-RU")} ₽/день`
            : "Показатель кликабельности",
        change: ctrChangeStr,
        isPositive: isCtrPositive,
        chartType: "line",
        chartPoints:
          ctrPoints.length >= 2
            ? ctrPoints
            : [weekCtr * 0.9, weekCtr, weekCtr * 1.05],
        chartDates: currentWeekDates,
        unit: "%",
        formatValue: (val: number) => `${val.toFixed(2)}%`,
      },
      {
        label: "Конверсии (CPA)",
        value: totalConversions.toLocaleString("ru-RU"),
        subValue:
          avgCPA > 0
            ? `Ср. цена: ${avgCPA.toLocaleString("ru-RU")} ₽`
            : "Целей не зафиксировано",
        change: cpaChangeStr,
        isPositive: isCpaPositive,
        chartType: "line",
        chartPoints:
          conversionPoints.length >= 2
            ? conversionPoints
            : [0, 0, totalConversions],
        chartDates: currentWeekDates,
        unit: "конв.",
        formatValue: (val: number) =>
          `${Math.round(val).toLocaleString("ru-RU")} конв.`,
      },
    ];
  }

  const problems = realCampaign?.problems || [];

  const campaign: Campaign | undefined = realCampaign
    ? {
        ...fallback,
        id: String(realCampaign.campaign_id),
        campaign_id: realCampaign.campaign_id,
        client_login: realCampaign.client_login,
        name: realCampaign.name,
        type: formatCampaignType(realCampaign.type),
        placement: formatPlacementTypes(realCampaign.placement_types),
        budget: weeklyBudget
          ? `${weeklyBudget.toLocaleString("ru-RU")} ₽/нед`
          : fallback?.budget || "Не задан",
        spent: `${Math.round(weekCost).toLocaleString("ru-RU")} ₽`,
        clicks: weekClicks.toLocaleString("ru-RU"),
        ctr: `${weekCtr}%`,
        metrics: liveMetrics || fallback?.metrics || [],
        history:
          realCampaign?.action_logs && realCampaign.action_logs.length > 0
            ? realCampaign.action_logs.map((log) => ({
                id: log.id,
                date: new Date(log.created_at).toLocaleString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                author:
                  log.event_type === "MODULE_EXECUTION"
                    ? "Автопилот"
                    : "Пользователь",
                action: log.title,
                oldValue: "—",
                newValue: log.description,
              }))
            : fallback?.history || [],
        problems,
      }
    : fallback;

  if (!campaign) {
    return <Navigate to="/" replace />;
  }

  // Проверка наличия подключенных модулей
  const activeRules = realCampaign?.active_rules || campaign.active_rules;
  const hasConnectedModules =
    activeRules && activeRules.length > 0
      ? activeRules.some((r) => r.is_enabled)
      : Boolean(campaign.activeModules && campaign.activeModules.length > 0);

  return (
    <div className="flex flex-col gap-6">
      <CampaignHeader campaign={campaign} />
      <CampaignMetrics
        metrics={campaign.metrics || []}
        campaignId={campaign.id}
      />

      {/* Алёрт под показателями */}
      {problems.length > 0 ? (
        <div className="flex flex-col gap-2">
          {problems.map((problem, pIdx) => (
            <Alert
              key={pIdx}
              status="danger"
              className="w-full rounded-2xl bg-rose-500/10 border border-rose-500/30"
            >
              <AlertIndicator />
              <AlertContent>
                <AlertTitle className="text-sm font-semibold text-rose-400">
                  {problem.module_name} (найдено {problem.entities_count}{" "}
                  неэффективных площадок)
                </AlertTitle>
                <AlertDescription className="text-xs mt-1">
                  {problem.description} Рекомендуется активировать
                  автоматическую очистку площадок для устранения нецелевого
                  расхода.
                </AlertDescription>
              </AlertContent>
            </Alert>
          ))}
        </div>
      ) : hasConnectedModules ? (
        <Alert status="accent">
          <AlertIndicator />
          <AlertContent>
            <AlertTitle>Модули оптимизации начали работу</AlertTitle>
            <AlertDescription>
              Нужно время для достижения эффективности и ощутимых результатов.
              Мы подготовим промежуточный отчёт и покажем его здесь.
            </AlertDescription>
          </AlertContent>
        </Alert>
      ) : (
        <Alert status="warning">
          <AlertIndicator />
          <AlertContent>
            <AlertTitle>Нет подключенных модулей оптимизации</AlertTitle>
            <AlertDescription>
              Подключите модули оптимизации ниже, чтобы автопилот начал
              автоматическую очистку неэффективных площадок и управление
              ставками.
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      <OptimizationModules
        campaignId={realCampaign?.campaign_id || campaign.id}
        rules={realCampaign?.active_rules || campaign.active_rules}
        problems={problems}
      />
      <CampaignHistory
        campaignId={realCampaign?.campaign_id || campaign.id}
        actionLogs={
          realCampaign
            ? actionsResponse?.data || realCampaign.action_logs || []
            : undefined
        }
        history={realCampaign ? undefined : fallback?.history}
        isLoading={isActionsLoading}
      />
    </div>
  );
};
